import { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Plus,
  Pencil,
  Trash2,
  Download,
  FileSignature,
  ArrowRightLeft,
} from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "@/lib/auth";
import { logError } from "@/lib/log";

const fmtEur = (n) => {
  const v = Number(n) || 0;
  return `${v
    .toFixed(2)
    .replace(".", ",")
    .replace(/\B(?=(\d{3})+(?!\d))/g, ".")} €`;
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  const s = String(iso);
  if (s.length >= 10 && s[4] === "-" && s[7] === "-") {
    return `${s.slice(8, 10)}/${s.slice(5, 7)}/${s.slice(0, 4)}`;
  }
  return s;
};

const todayIso = () => new Date().toISOString().slice(0, 10);

const addDays = (isoDate, days) => {
  const d = new Date(isoDate);
  if (Number.isNaN(d.getTime())) return "";
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

const emptyLine = () => ({ description: "", units: 1, unit_price: 0, vat_rate: 0 });

const emptyForm = () => ({
  client_id: "",
  date: todayIso(),
  due_date: addDays(todayIso(), 30),
  lines: [emptyLine()],
  notes: "",
});

export default function QuotesSection() {
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [convertTarget, setConvertTarget] = useState(null);
  const [converting, setConverting] = useState(false);
  const [nextNumber, setNextNumber] = useState("");
  const [form, setForm] = useState(emptyForm());

  const fetchAll = useCallback(async () => {
    try {
      const [q, c, p, n] = await Promise.all([
        adminApi.get("/admin/invoicing/quotes"),
        adminApi.get("/admin/invoicing/clients"),
        adminApi.get("/admin/invoicing/products"),
        adminApi.get("/admin/invoicing/quotes/counters/next-number"),
      ]);
      setItems(q.data || []);
      setClients(c.data || []);
      setProducts(p.data || []);
      setNextNumber(n.data?.next_number || "");
    } catch (e) {
      logError("Error cargando presupuestos", e);
      toast.error("No se pudieron cargar los presupuestos.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const openTotal = useMemo(
    () =>
      items
        .filter((q) => !q.invoice_id)
        .reduce((acc, q) => acc + (Number(q.total) || 0), 0),
    [items]
  );

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm());
    setDialogOpen(true);
  };

  const openEdit = (q) => {
    setEditingId(q.id);
    setForm({
      client_id: q.client_id || "",
      date: q.date || todayIso(),
      due_date: q.due_date || "",
      lines: (q.lines || []).map((l) => ({
        description: l.description || "",
        units: l.units ?? 1,
        unit_price: l.unit_price ?? 0,
        vat_rate: l.vat_rate ?? 0,
      })),
      notes: q.notes || "",
    });
    setDialogOpen(true);
  };

  const handleLineChange = (idx, field, value) => {
    const lines = [...form.lines];
    lines[idx] = { ...lines[idx], [field]: value };
    setForm({ ...form, lines });
  };

  const insertProduct = (idx, product) => {
    if (!product) return;
    const lines = [...form.lines];
    lines[idx] = {
      ...lines[idx],
      description: product.name || "",
      unit_price: product.unit_price ?? 0,
      vat_rate: product.vat_rate ?? 0,
    };
    setForm({ ...form, lines });
  };

  const handleSave = async () => {
    if (!form.client_id) {
      toast.error("Elige el cliente del presupuesto.");
      return;
    }
    const lines = form.lines
      .filter((l) => (l.description || "").trim())
      .map((l) => ({
        description: l.description.trim(),
        units: Math.max(1, parseInt(l.units, 10) || 1),
        unit_price: Number(l.unit_price) || 0,
        vat_rate: Number(l.vat_rate) || 0,
      }));
    if (lines.length === 0) {
      toast.error("Añade al menos un concepto.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        client_id: form.client_id,
        date: form.date,
        due_date: form.due_date,
        lines,
        notes: form.notes,
      };
      if (editingId) {
        await adminApi.put(`/admin/invoicing/quotes/${editingId}`, payload);
        toast.success("Presupuesto actualizado.");
      } else {
        const res = await adminApi.post("/admin/invoicing/quotes", payload);
        toast.success(`Presupuesto ${res.data?.number || ""} creado.`);
      }
      setDialogOpen(false);
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar el presupuesto.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await adminApi.delete(`/admin/invoicing/quotes/${deleteTarget.id}`);
      toast.success("Presupuesto eliminado.");
      setDeleteTarget(null);
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo eliminar.");
    }
  };

  const handleConvert = async () => {
    if (!convertTarget) return;
    setConverting(true);
    try {
      const res = await adminApi.post(
        `/admin/invoicing/quotes/${convertTarget.id}/convert`
      );
      toast.success(`Factura ${res.data?.number || ""} creada desde el presupuesto.`);
      setConvertTarget(null);
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo convertir en factura.");
    } finally {
      setConverting(false);
    }
  };

  const downloadPdf = async (q) => {
    try {
      const res = await adminApi.get(`/admin/invoicing/quotes/${q.id}/pdf`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(
        new Blob([res.data], { type: "application/pdf" })
      );
      const a = document.createElement("a");
      a.href = url;
      a.download = `Presupuesto_${q.number || q.id.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) {
      logError("Error descargando PDF del presupuesto", e);
      toast.error("No se pudo descargar el PDF.");
    }
  };

  const formTotals = useMemo(() => {
    let subtotal = 0;
    let vat = 0;
    form.lines.forEach((l) => {
      const base = (parseInt(l.units, 10) || 0) * Number(l.unit_price || 0);
      subtotal += base;
      vat += base * (Number(l.vat_rate || 0) / 100);
    });
    return { subtotal, vat, total: subtotal + vat };
  }, [form.lines]);

  return (
    <div data-testid="invoicing-quotes-section">
      <div className="flex items-center justify-between mb-3 gap-4 flex-wrap">
        <div>
          <h3 className="heading-font text-xl font-bold text-zinc-950">
            Presupuestos
          </h3>
          <p className="text-sm text-zinc-500">
            Pendientes de aceptar:{" "}
            <b className="text-zinc-800 tabular-nums">{fmtEur(openTotal)}</b>
            {nextNumber && (
              <span className="ml-2 text-xs text-zinc-400">
                Siguiente número: <b>{nextNumber}</b>
              </span>
            )}
          </p>
        </div>
        <Button
          className="h-10 bg-zinc-950 hover:bg-zinc-800 text-white"
          onClick={openCreate}
          data-testid="invoicing-quotes-create-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          Nuevo presupuesto
        </Button>
      </div>

      {loading ? (
        <div className="text-center text-zinc-500 py-10">Cargando…</div>
      ) : items.length === 0 ? (
        <div className="text-center text-zinc-500 py-12 border-2 border-dashed border-zinc-300 rounded-md">
          <FileSignature className="h-12 w-12 mx-auto mb-3 text-zinc-400" />
          <p className="font-semibold text-zinc-700">Aún no hay presupuestos</p>
          <p className="text-sm mt-2 max-w-md mx-auto">
            Pulsa «Nuevo presupuesto» para preparar una oferta. Cuando el
            cliente la acepte, la conviertes en factura con un clic.
          </p>
        </div>
      ) : (
        <div className="border border-zinc-200 rounded-md overflow-auto max-h-[68vh]">
          <table className="w-full text-sm min-w-[860px]">
            <thead className="bg-zinc-100 text-zinc-700 sticky top-0 z-10 shadow-[0_1px_0_0_rgba(0,0,0,0.08)]">
              <tr>
                <th className="text-left px-3 py-2 font-semibold">Nº</th>
                <th className="text-left px-3 py-2 font-semibold">Fecha</th>
                <th className="text-left px-3 py-2 font-semibold">Cliente</th>
                <th className="text-right px-3 py-2 font-semibold">Total</th>
                <th className="text-center px-3 py-2 font-semibold">Estado</th>
                <th className="text-center px-3 py-2 font-semibold w-[260px]">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody>
              {items.map((q) => (
                <tr
                  key={q.id}
                  className="border-t border-zinc-200 hover:bg-zinc-50"
                  data-testid={`invoicing-quote-row-${q.id}`}
                >
                  <td className="px-3 py-2 font-mono font-semibold text-zinc-900 whitespace-nowrap">
                    {q.number || "—"}
                  </td>
                  <td className="px-3 py-2 text-zinc-700">
                    {fmtDate(q.date)}
                    {q.due_date && (
                      <div className="text-[11px] text-zinc-500">
                        Válido hasta {fmtDate(q.due_date)}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-zinc-900">
                    <div className="font-semibold">
                      {q.client_snapshot?.name || "—"}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-right font-semibold tabular-nums whitespace-nowrap">
                    {fmtEur(q.total)}
                  </td>
                  <td className="px-3 py-2 text-center">
                    {q.invoice_id ? (
                      <span
                        className="inline-block text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-green-100 text-green-800 border border-green-300"
                        title={`Factura ${q.invoice_number}`}
                        data-testid={`invoicing-quote-invoiced-badge-${q.id}`}
                      >
                        FACTURADO {q.invoice_number}
                      </span>
                    ) : (
                      <span
                        className="inline-block text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-300"
                        data-testid={`invoicing-quote-open-badge-${q.id}`}
                      >
                        PENDIENTE
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center justify-center gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => downloadPdf(q)}
                        title="Descargar PDF"
                        data-testid={`invoicing-quote-pdf-${q.id}`}
                      >
                        <Download className="h-4 w-4" />
                      </Button>
                      {!q.invoice_id && (
                        <>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setConvertTarget(q)}
                            title="Convertir en factura"
                            className="text-green-700 hover:bg-green-50 hover:text-green-900"
                            data-testid={`invoicing-quote-convert-${q.id}`}
                          >
                            <ArrowRightLeft className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openEdit(q)}
                            title="Editar"
                            data-testid={`invoicing-quote-edit-${q.id}`}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setDeleteTarget(q)}
                        title="Eliminar"
                        className="text-red-600 hover:bg-red-50 hover:text-red-800"
                        data-testid={`invoicing-quote-delete-${q.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Alta / edición */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent
          className="max-w-4xl max-h-[90vh] overflow-y-auto"
          data-testid="invoicing-quote-dialog"
        >
          <DialogHeader>
            <DialogTitle>
              {editingId ? "Editar presupuesto" : "Nuevo presupuesto"}
            </DialogTitle>
            <DialogDescription>
              El presupuesto se numera solo ({nextNumber || "P2026-0001"}). Cuando
              el cliente lo acepte, conviértelo en factura desde el listado.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="md:col-span-1">
                <label className="block text-sm font-medium text-zinc-700 mb-1">
                  Cliente *
                </label>
                <Select
                  value={form.client_id || undefined}
                  onValueChange={(v) => setForm({ ...form, client_id: v })}
                >
                  <SelectTrigger
                    className="h-10"
                    data-testid="invoicing-quote-client-select"
                  >
                    <SelectValue placeholder="Selecciona cliente…" />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    {clients.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}
                        {c.nif ? ` — ${c.nif}` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-1">
                  Fecha *
                </label>
                <Input
                  type="date"
                  value={form.date}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      date: e.target.value,
                      due_date: addDays(e.target.value, 30),
                    })
                  }
                  className="h-10"
                  data-testid="invoicing-quote-date"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-1">
                  Válido hasta
                </label>
                <Input
                  type="date"
                  value={form.due_date}
                  onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                  className="h-10"
                  data-testid="invoicing-quote-due-date"
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-sm font-medium text-zinc-700">
                  Conceptos
                </label>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setForm({ ...form, lines: [...form.lines, emptyLine()] })}
                  data-testid="invoicing-quote-add-line"
                >
                  <Plus className="h-3 w-3 mr-1" /> Añadir línea
                </Button>
              </div>
              <div className="border border-zinc-200 rounded-md overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-zinc-50 text-zinc-600 text-xs">
                    <tr>
                      <th className="text-left px-2 py-1 font-semibold w-1/2">Concepto</th>
                      <th className="text-right px-2 py-1 font-semibold w-16">Uds.</th>
                      <th className="text-right px-2 py-1 font-semibold w-28">Base ud.</th>
                      <th className="text-right px-2 py-1 font-semibold w-16">% IVA</th>
                      <th className="text-right px-2 py-1 font-semibold w-24">Total</th>
                      <th className="text-center px-2 py-1 w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {form.lines.map((l, idx) => {
                      const base =
                        (parseInt(l.units, 10) || 0) * Number(l.unit_price || 0);
                      const total = base * (1 + Number(l.vat_rate || 0) / 100);
                      return (
                        <tr key={idx} className="border-t border-zinc-100 align-top">
                          <td className="px-2 py-1">
                            <Textarea
                              rows={1}
                              value={l.description}
                              onChange={(e) =>
                                handleLineChange(idx, "description", e.target.value)
                              }
                              className="min-h-[36px] text-sm"
                              placeholder="Descripción del concepto…"
                              data-testid={`invoicing-quote-line-desc-${idx}`}
                            />
                            {products.length > 0 && (
                              <Select
                                value=""
                                onValueChange={(v) =>
                                  insertProduct(idx, products.find((p) => p.id === v))
                                }
                              >
                                <SelectTrigger
                                  className="h-7 mt-1 text-xs text-zinc-500 border-dashed"
                                  data-testid={`invoicing-quote-line-product-${idx}`}
                                >
                                  <SelectValue placeholder="Insertar del catálogo…" />
                                </SelectTrigger>
                                <SelectContent className="max-h-72">
                                  {products.map((p) => (
                                    <SelectItem key={p.id} value={p.id}>
                                      {p.name} — {fmtEur(p.unit_price)}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            )}
                          </td>
                          <td className="px-2 py-1">
                            <Input
                              type="number"
                              step="1"
                              min="1"
                              value={l.units}
                              onChange={(e) =>
                                handleLineChange(
                                  idx,
                                  "units",
                                  e.target.value.replace(/[^0-9]/g, "")
                                )
                              }
                              className="h-9 text-right"
                              data-testid={`invoicing-quote-line-units-${idx}`}
                            />
                          </td>
                          <td className="px-2 py-1">
                            <Input
                              type="number"
                              step="0.01"
                              min="0"
                              value={l.unit_price}
                              onChange={(e) =>
                                handleLineChange(idx, "unit_price", e.target.value)
                              }
                              className="h-9 text-right"
                              data-testid={`invoicing-quote-line-price-${idx}`}
                            />
                          </td>
                          <td className="px-2 py-1">
                            <Input
                              type="number"
                              step="1"
                              min="0"
                              max="100"
                              value={l.vat_rate}
                              onChange={(e) =>
                                handleLineChange(idx, "vat_rate", e.target.value)
                              }
                              className="h-9 text-right"
                              data-testid={`invoicing-quote-line-vat-${idx}`}
                            />
                          </td>
                          <td className="px-2 py-1 text-right font-semibold tabular-nums pt-3">
                            {fmtEur(total)}
                          </td>
                          <td className="px-1 py-1 text-center pt-3">
                            <button
                              type="button"
                              onClick={() =>
                                setForm({
                                  ...form,
                                  lines: form.lines.filter((_, i) => i !== idx),
                                })
                              }
                              className="text-red-600 hover:text-red-800"
                              title="Quitar línea"
                              data-testid={`invoicing-quote-line-remove-${idx}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot className="bg-zinc-50 text-sm">
                    <tr className="border-t border-zinc-200">
                      <td colSpan={4} className="px-2 py-1 text-right text-zinc-600">
                        Base imponible
                      </td>
                      <td className="px-2 py-1 text-right tabular-nums">
                        {fmtEur(formTotals.subtotal)}
                      </td>
                      <td />
                    </tr>
                    <tr>
                      <td colSpan={4} className="px-2 py-1 text-right text-zinc-600">
                        IVA
                      </td>
                      <td className="px-2 py-1 text-right tabular-nums">
                        {fmtEur(formTotals.vat)}
                      </td>
                      <td />
                    </tr>
                    <tr className="border-t border-zinc-200">
                      <td colSpan={4} className="px-2 py-1 text-right font-bold">
                        TOTAL
                      </td>
                      <td
                        className="px-2 py-1 text-right font-bold tabular-nums"
                        data-testid="invoicing-quote-total"
                      >
                        {fmtEur(formTotals.total)}
                      </td>
                      <td />
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-1">
                Notas
              </label>
              <Textarea
                rows={2}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Condiciones, validez de la oferta…"
                data-testid="invoicing-quote-notes"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDialogOpen(false)}
              disabled={saving}
              data-testid="invoicing-quote-cancel-btn"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-zinc-950 hover:bg-zinc-800 text-white"
              data-testid="invoicing-quote-save-btn"
            >
              {saving ? "Guardando…" : editingId ? "Guardar cambios" : "Crear presupuesto"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Convertir en factura */}
      <AlertDialog
        open={!!convertTarget}
        onOpenChange={(o) => !o && setConvertTarget(null)}
      >
        <AlertDialogContent data-testid="invoicing-quote-convert-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              Convertir {convertTarget?.number} en factura
            </AlertDialogTitle>
            <AlertDialogDescription>
              Se creará una factura con los mismos conceptos e importes
              ({fmtEur(convertTarget?.total)}) a nombre de{" "}
              {convertTarget?.client_snapshot?.name}. El presupuesto quedará
              marcado como facturado.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={converting}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConvert}
              disabled={converting}
              className="bg-green-700 hover:bg-green-800"
              data-testid="invoicing-quote-convert-confirm"
            >
              {converting ? "Creando…" : "Crear factura"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Eliminar */}
      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(o) => !o && setDeleteTarget(null)}
      >
        <AlertDialogContent data-testid="invoicing-quote-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              Eliminar presupuesto {deleteTarget?.number}
            </AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminará permanentemente. Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="invoicing-quote-delete-confirm"
            >
              Sí, eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
